import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';

export interface Carrier {
  id: number;
  code: string;
  name: string;
  displayName: string;
  description?: string;
  country?: string;
  website?: string;
  phone?: string;
  email?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCarrierRequest {
  code: string;
  name: string;
  displayName: string;
  description?: string;
  country?: string;
  website?: string;
  phone?: string;
  email?: string;
}

export interface UpdateCarrierRequest {
  code?: string;
  name?: string;
  displayName?: string;
  description?: string;
  country?: string;
  website?: string;
  phone?: string;
  email?: string;
}

export interface CarrierSearchRequest {
  name?: string;
  code?: string;
  isActive?: boolean;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

class CarrierService {
  private baseUrl = '/api/carriers';

  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    return {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
  }

  // Получение всех перевозчиков с пагинацией
  async getCarriers(page = 0, size = 20, sortBy = 'id', sortDir = 'ASC'): Promise<PaginatedResponse<Carrier>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });
    
    const url = getDataApiUrl(`${this.baseUrl}?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение только активных перевозчиков
  async getActiveCarriers(): Promise<Carrier[]> {
    const url = getDataApiUrl(`${this.baseUrl}/active`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение перевозчика по ID
  async getCarrierById(id: number): Promise<Carrier> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Создание нового перевозчика
  async createCarrier(carrierData: CreateCarrierRequest): Promise<Carrier> {
    const url = getDataApiUrl(this.baseUrl);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(carrierData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Обновление перевозчика
  async updateCarrier(id: number, carrierData: UpdateCarrierRequest): Promise<Carrier> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(carrierData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Удаление перевозчика
  async deleteCarrier(id: number): Promise<void> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  // Поиск перевозчиков
  async searchCarriers(searchData: CarrierSearchRequest, page = 0, size = 20): Promise<PaginatedResponse<Carrier>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    const url = getDataApiUrl(`${this.baseUrl}/search?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(searchData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение стандартных перевозчиков
  getDefaultCarriers(): CreateCarrierRequest[] {
    return [
      {
        code: "KC",
        name: "Air Astana",
        displayName: "Air Astana",
        description: "Национальный авиаперевозчик Казахстана",
        country: "Казахстан",
        website: "https://www.airastana.com",
        phone: "+7 727 244 44 44",
        email: "info@airastana.com"
      },
      {
        code: "DV",
        name: "Scat Airlines",
        displayName: "SCAT Airlines",
        description: "Частная авиакомпания Казахстана",
        country: "Казахстан",
        website: "https://www.scat.kz",
        phone: "+7 727 244 44 44",
        email: "info@scat.kz"
      },
      {
        code: "IQ",
        name: "Qazaq Air",
        displayName: "Qazaq Air",
        description: "Региональная авиакомпания Казахстана",
        country: "Казахстан",
        website: "https://www.qazaqair.com",
        phone: "+7 727 244 44 44",
        email: "info@qazaqair.com"
      }
    ];
  }
}

const carrierService = new CarrierService();
export default carrierService; 